/* ══════════════════════════════════════════════════════════════════════════
   HAVANI — FEED

   Two elements, both only requested by the sections that need them.

     hv-post-video   one video in a post: muted autoplay while it is genuinely
                     on screen, paused the moment it is not, sound only ever on
                     a deliberate press.
     hv-feed-grid    the Feed's type filter and its "keep scrolling" paging.

   No framework, no dependency, and no scroll hijacking: the page scrolls the
   way the browser scrolls it. IntersectionObserver decides what plays, and
   nothing here ever calls scrollTo, snaps, or intercepts a wheel event.

   Lifecycle: Shopify re-renders a section's HTML in place, and the theme
   editor MOVES a section's node when a merchant reorders it — which
   disconnects and reconnects this same instance. Every listener goes through
   an AbortController and every observer is disconnected on the way out, so a
   reconnect can never leave a second observer watching the same video.
   ══════════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';
  const $ = (s, r) => (r || document).querySelector(s);
  const $$ = (s, r) => Array.from((r || document).querySelectorAll(s));
  const reduced = () => matchMedia('(prefers-reduced-motion:reduce)').matches;
  const strings = () => window.hvStrings || {};

  /* ── one video in a post ───────────────────────────────────────────────
     60% is the threshold the design asks for: a clip that is half off the
     bottom of the screen is not being watched, and starting it there is what
     makes a feed feel like it is playing at you rather than for you.       */
  class HvPostVideo extends HTMLElement {
    connectedCallback() {
      this.video = $('video', this);
      if (!this.video) return;

      /* teardown first — see the file header */
      this.teardown();
      this.controller = new AbortController();
      const signal = this.controller.signal;

      this.video.muted = true;          /* belt and braces: autoplay is only
                                           ever allowed while muted */
      this.video.setAttribute('muted', '');

      if (reduced()) {
        /* Reduced motion gets the poster and an explicit control. Nothing
           starts on its own, and the element never observes anything. */
        this.buildControl('play', signal);
        return;
      }

      this.buildControl('sound', signal);

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            /* The threshold list fires on each crossing, so guard: calling
               play() on a playing video is a no-op, but asking for it twice
               per scroll is still two decisions where there was one. */
            if (this.video.paused) {
              /* preload="none" means the first play() is also the first fetch */
              this.video.play().catch(() => { /* a blocked autoplay is not an error */ });
            }
          } else {
            this.video.pause();
          }
        });
      }, { threshold: [0, 0.6, 1] });
      this.observer.observe(this);

      /* If the tab goes away, so does the playback. */
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.video.pause();
      }, { signal });
    }

    disconnectedCallback() { this.teardown(); }

    teardown() {
      if (this.controller) { this.controller.abort(); this.controller = null; }
      if (this.observer) { this.observer.disconnect(); this.observer = null; }
      if (this.button) { this.button.remove(); this.button = null; }
    }

    /* The control is built here rather than in Liquid because which control a
       post gets depends on the visitor's motion preference, which Liquid
       cannot see. Without JavaScript the poster simply sits there — honest,
       and never a dead button. */
    buildControl(kind, signal) {
      /* Idempotent on purpose. This element can be initialised over markup
         that already contains a control — a re-render that captured the live
         DOM rather than the server's, for instance — and appending a second
         button there would leave a dead one behind. */
      this.querySelectorAll('.post__sound, .post__play').forEach((old) => old.remove());
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = kind === 'play' ? 'post__play' : 'post__sound';
      const label = () =>
        kind === 'play'
          ? (strings().feedPlay || 'Play video')
          : (this.video.muted ? (strings().soundOn || 'Turn sound on')
                              : (strings().soundOff || 'Turn sound off'));
      const paint = () => {
        btn.setAttribute('aria-label', label());
        btn.setAttribute('aria-pressed', String(!this.video.muted));
        btn.textContent = kind === 'play'
          ? (this.video.paused ? '▶' : '‖')
          : (this.video.muted ? '♪' : '♫');
      };
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (kind === 'play') {
          if (this.video.paused) this.video.play().catch(() => {});
          else this.video.pause();
        } else {
          /* Sound is ALWAYS a deliberate press. It is never restored from a
             previous visit and never inferred. */
          this.video.muted = !this.video.muted;
          if (!this.video.muted) this.video.play().catch(() => {});
        }
        paint();
      }, { signal });
      this.video.addEventListener('play', paint, { signal });
      this.video.addEventListener('pause', paint, { signal });
      paint();
      this.appendChild(btn);
      this.button = btn;
    }
  }
  if (!customElements.get('hv-post-video')) customElements.define('hv-post-video', HvPostVideo);

  /* ── the Feed grid ─────────────────────────────────────────────────────
     Filtering by type and a real "keep scrolling" link. Paging is a button
     that reveals more, not an infinite-scroll hijack — the visitor stays in
     charge of how far down they go.                                       */
  class HvFeedGrid extends HTMLElement {
    connectedCallback() {
      this.posts = $$('[data-feed-item]', this);
      if (!this.posts.length) return;

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;

      this.perPage = Number(this.dataset.perPage || 12);
      this.shown = this.perPage;
      this.filter = 'all';

      this.buttons = $$('[data-feed-filter]', this);
      this.buttons.forEach((btn) => {
        btn.addEventListener('click', () => {
          this.buttons.forEach((b) => b.setAttribute('aria-pressed', 'false'));
          btn.setAttribute('aria-pressed', 'true');
          this.filter = btn.dataset.feedFilter || 'all';
          this.shown = this.perPage;
          this.paint();
        }, { signal });
      });

      this.more = $('[data-feed-more]', this);
      this.more?.addEventListener('click', () => {
        this.shown += this.perPage;
        this.paint();
      }, { signal });

      this.paint();
    }

    disconnectedCallback() {
      if (this.controller) { this.controller.abort(); this.controller = null; }
    }

    get matching() {
      if (this.filter === 'all') return this.posts;
      return this.posts.filter((p) => (p.dataset.feedType || '') === this.filter);
    }

    paint() {
      const list = this.matching;
      /* hidden, not removed: a filtered post keeps its place in the DOM so the
         grid's dense packing stays stable when the filter is cleared */
      this.posts.forEach((p) => { p.hidden = true; });
      list.slice(0, this.shown).forEach((p) => { p.hidden = false; });

      const done = this.shown >= list.length;
      if (this.more) {
        this.more.hidden = done;
        this.more.disabled = done;
      }
      const tally = $('[data-feed-tally]', this);
      if (tally) tally.textContent = Math.min(this.shown, list.length) + ' / ' + list.length;
    }
  }
  if (!customElements.get('hv-feed-grid')) customElements.define('hv-feed-grid', HvFeedGrid);
})();
